import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  animate
} from 'motion/react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import {
  SPRING_DEFAULT,
  SPRING_SHEET,
  SPRING_PRESS,
  REDUCED_MOTION_TRANSITION,
  project,
  nearestSnapPoint
} from '../utils/springs';
import './Header.css';

const NAV_LINKS = [
  { to: '/', key: 'navHome', end: true },
  { to: '/about', key: 'navAbout' },
  { to: '/products', key: 'navAllProducts' },
  { to: '/products?category=hazelnuts', key: 'navFreshHazelnuts' },
  { to: '/products?category=snacks', key: 'navHazelnutSnaps' },
  { to: '/products?category=truffles', key: 'navWhiteTruffles' }
];

const COMPACT_HEADER_QUERY = '(max-width: 1100px)';

const useCompactHeader = () => {
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(COMPACT_HEADER_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(COMPACT_HEADER_QUERY);
    const handleChange = () => setIsCompact(mediaQuery.matches);
    handleChange();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return isCompact;
};

const Header = () => {
  const { cartCount } = useCart();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const reduceMotion = useReducedMotion();
  const mobileMenuRef = useRef(null);
  const sheetFlickRef = useRef(false);
  const [menuDragLeft, setMenuDragLeft] = useState(-320);
  const langSelectorRef = useRef(null);
  const mobileLangSelectorRef = useRef(null);
  const menuToggleRef = useRef(null);

  const { language, setLanguage, t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const isCompactHeader = useCompactHeader();

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const menu = mobileMenuRef.current;
    const focusable = menu?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const closeBtn = menu?.querySelector('.mobile-menu-close');
    closeBtn?.focus();
    const toggleEl = menuToggleRef.current;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        return;
      }

      if (event.key !== 'Tab' || !focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      if (toggleEl instanceof HTMLElement) {
        toggleEl.focus();
      }
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsLangOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (isMenuOpen) {
      sheetFlickRef.current = false;
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const measureMenuWidth = () => {
      const width = mobileMenuRef.current?.offsetWidth;
      if (width) {
        setMenuDragLeft(-width);
      }
    };

    measureMenuWidth();
    window.addEventListener('resize', measureMenuWidth);
    return () => window.removeEventListener('resize', measureMenuWidth);
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isLangOpen) return undefined;

    const handlePointerDown = (event) => {
      const inDesktop = langSelectorRef.current?.contains(event.target);
      const inMobile = mobileLangSelectorRef.current?.contains(event.target);
      if (!inDesktop && !inMobile) {
        setIsLangOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsLangOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isLangOpen]);

  const handleLinkClick = () => {
    setIsMenuOpen(false);
    setIsLangOpen(false);
  };

  const languages = [
    { code: 'ro', name: 'Română', flag: '🇷🇴' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ];

  const currentLang = languages.find((l) => l.code === language);

  const headerTransition = reduceMotion ? REDUCED_MOTION_TRANSITION : SPRING_DEFAULT;

  const handleSheetDragEnd = (_event, info) => {
    const menuEl = mobileMenuRef.current;
    const width = menuEl?.offsetWidth || 280;
    const projected = info.offset.x + project(info.velocity.x);
    const snap = nearestSnapPoint(projected, [0, -width]);
    const spring = sheetFlickRef.current ? SPRING_SHEET : SPRING_DEFAULT;
    sheetFlickRef.current = false;

    if (snap <= -width / 2) {
      setIsMenuOpen(false);
      return;
    }

    if (menuEl) {
      animate(menuEl, { x: 0 }, spring);
    }
  };

  const renderCartBadge = (className) =>
    cartCount > 0 ? (
      <motion.span
        className={className}
        key={cartCount}
        initial={reduceMotion ? { opacity: 0 } : { scale: 0.85 }}
        animate={reduceMotion ? { opacity: 1 } : { scale: 1 }}
        transition={reduceMotion ? REDUCED_MOTION_TRANSITION : SPRING_PRESS}
      >
        {cartCount}
      </motion.span>
    ) : null;

  const isNavActive = (to, end) => {
    if (end) {
      return location.pathname === '/' && !location.search;
    }
    if (to.includes('?')) {
      return `${location.pathname}${location.search}` === to;
    }
    return location.pathname === to && !location.search;
  };

  const renderLogoLink = () => (
    <Link
      to="/"
      className="logo"
      aria-label={t('header.logoAria')}
      onClick={() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setIsMenuOpen(false);
      }}
    >
      <img
        src={isDark ? '/images/logo/logo-dark.png' : '/images/logo/logo-light.png'}
        alt="deezNuts"
        className="logo-img"
        loading="lazy"
      />
    </Link>
  );

  const renderThemeToggle = () => (
    <button
      type="button"
      className="theme-toggle"
      onClick={toggleTheme}
      aria-label={isDark ? t('header.themeLight') : t('header.themeDark')}
      title={isDark ? t('header.themeLight') : t('header.themeDark')}
    >
      {isDark ? (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </button>
  );

  const renderLanguageSelector = (selectorRef, compact = false) => (
    <div
      className={`language-selector${compact ? ' language-selector--compact' : ''}`}
      ref={selectorRef}
    >
      <button
        type="button"
        className="lang-btn"
        onClick={() => setIsLangOpen(!isLangOpen)}
        aria-expanded={isLangOpen}
        aria-haspopup="listbox"
        aria-label={t('header.mobileLang')}
      >
        <span className="lang-flag">{currentLang?.flag}</span>
        <span className="lang-name">{currentLang?.name}</span>
        <span className="arrow" aria-hidden="true">▼</span>
      </button>
      <AnimatePresence>
        {isLangOpen && (
          <motion.div
            className="lang-dropdown"
            role="listbox"
            initial={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.95, y: -6 }
            }
            animate={
              reduceMotion
                ? { opacity: 1 }
                : { opacity: 1, scale: 1, y: 0 }
            }
            exit={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, scale: 0.95, y: -6 }
            }
            transition={headerTransition}
            style={{ transformOrigin: 'top right' }}
          >
            {languages.map((lang) => (
              <button
                type="button"
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setIsLangOpen(false);
                }}
                className={language === lang.code ? 'active' : ''}
                role="option"
                aria-selected={language === lang.code}
              >
                <span className="lang-flag">{lang.flag}</span>
                {lang.name}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <>
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            <p className="promo-text">{t('header.promo')}</p>
            <Link to="/products?category=hazelnuts" className="new-products-link">
              {t('header.newProductsLink')}
            </Link>
          </div>
        </div>
      </div>

      <motion.header
        className={`main-header${isScrolled ? ' main-header--scrolled' : ''}`}
        animate={{ y: 0, opacity: 1 }}
        transition={headerTransition}
      >
        <div className="container">
          {isCompactHeader ? (
            <div className="header-mobile">
              <div className="header-mobile-slot header-mobile-slot--start">
                <button
                  type="button"
                  ref={menuToggleRef}
                  className="menu-toggle"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-label={t('header.toggleMenu')}
                  aria-expanded={isMenuOpen}
                >
                  <span />
                  <span />
                  <span />
                </button>
              </div>

              <div className="header-mobile-slot header-mobile-slot--center">
                {renderLogoLink()}
              </div>

              <div className="header-mobile-slot header-mobile-slot--end">
                <Link
                  to="/cart"
                  className="cart-icon cart-icon--mobile-bar"
                  aria-label={t('header.cart')}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                  </svg>
                  {renderCartBadge('cart-badge')}
                </Link>
                {renderLanguageSelector(mobileLangSelectorRef, true)}
                {renderThemeToggle()}
              </div>
            </div>
          ) : (
            <div className="header-desktop">
              {renderLogoLink()}

              <nav className="main-nav" aria-label={t('header.navLabel')}>
                <ul>
                  {NAV_LINKS.map(({ to, key, end }) => (
                    <li key={to}>
                      <Link
                        to={to}
                        className={isNavActive(to, end) ? 'active' : ''}
                        onClick={handleLinkClick}
                      >
                        {t(`header.${key}`)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="header-actions">
                {renderLanguageSelector(langSelectorRef)}
                {renderThemeToggle()}
                <Link to="/cart" className="cart-icon" aria-label={t('header.cart')}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                  </svg>
                  {renderCartBadge('cart-badge')}
                </Link>
                <Link
                  to="/products#products-page-search"
                  className="icon-btn"
                  aria-label={t('header.search')}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          )}
        </div>
      </motion.header>

      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              className="menu-overlay"
              onClick={() => setIsMenuOpen(false)}
              aria-hidden="true"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={headerTransition}
            />
            <motion.nav
              ref={mobileMenuRef}
              className="mobile-menu"
              aria-hidden={false}
              aria-label={t('header.navLabel')}
              initial={
                reduceMotion
                  ? { opacity: 0, x: 0 }
                  : { x: '-100%', opacity: 1 }
              }
              animate={
                reduceMotion
                  ? { opacity: 1, x: 0 }
                  : { x: 0, opacity: 1 }
              }
              exit={
                reduceMotion
                  ? { opacity: 0, x: 0 }
                  : { x: '-100%', opacity: 1 }
              }
              transition={headerTransition}
              drag={reduceMotion ? false : 'x'}
              dragConstraints={{ left: menuDragLeft, right: 0 }}
              dragElastic={{ left: 0.08, right: 0.55 }}
              onDrag={(_e, info) => {
                if (Math.abs(info.velocity.x) > 120) {
                  sheetFlickRef.current = true;
                }
              }}
              onDragEnd={handleSheetDragEnd}
            >
        <div className="mobile-menu-top">
          <button
            type="button"
            className="mobile-menu-close"
            onClick={() => setIsMenuOpen(false)}
            aria-label={t('header.close')}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <div className="mobile-menu-icons">
            <Link to="/cart" className="mobile-menu-icon" onClick={handleLinkClick}>
              <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
              </svg>
              <span>{t('header.cart')}</span>
              {renderCartBadge('mobile-cart-badge')}
            </Link>
            <Link
              to="/products#products-page-search"
              className="mobile-menu-icon"
              onClick={handleLinkClick}
            >
              <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
              <span>{t('header.search')}</span>
            </Link>
          </div>

          <div className="mobile-nav">
            <ul>
              {NAV_LINKS.map(({ to, key, end }) => (
                <li key={to}>
                  <Link
                    to={to}
                    onClick={handleLinkClick}
                    className={isNavActive(to, end) ? 'active' : ''}
                  >
                    {t(`header.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>

    </>
  );
};

export default Header;
